import { useMemo } from 'react';
import { useApiClient, useFetchInfiniteES } from '@marageti/z4-lib';
import {
  FilterOp, FilterConstraint, SearchRequest, SearchResult,
} from '@marageti/z4-sdk/lib/entity';
import { SalesStageAudit, SalesStageFilters } from '../types';

const DEFAULT_LIMIT = 100;

const buildFilterBy = (filters: SalesStageFilters): FilterConstraint[] => {
  const filterBy: FilterConstraint[] = [];

  if (filters.tripMatchId) {
    filterBy.push({ field: 'tripMatchId', value: [filters.tripMatchId] });
  }
  if (filters.partnerId) {
    filterBy.push({ field: 'partnerId', value: [filters.partnerId] });
  }
  if (filters.assignedAgentId) {
    filterBy.push({ field: 'assignedAgentId', value: [filters.assignedAgentId] });
  }
  if (filters.stage) {
    filterBy.push({ field: 'stage', value: [filters.stage] });
  }
  if (filters.actionTaken) {
    filterBy.push({ field: 'actionTaken', value: [filters.actionTaken] });
  }
  if (filters.startDate) {
    const endOfDay = `${filters.startDate}T23:59:59`;
    filterBy.push({ field: 'startAt', op: FilterOp.FilterOpBetween, value: [filters.startDate, endOfDay] });
  }
  if (filters.dueAt) {
    const endOfDay = `${filters.dueAt}T23:59:59`;
    filterBy.push({ field: 'dueAt', op: FilterOp.FilterOpBetween, value: [filters.dueAt, endOfDay] });
  }
  if (filters.overdue) {
    filterBy.push({ field: 'overdue', op: FilterOp.FilterOpTerm, value: [filters.overdue] });
  }

  return filterBy;
};

export const useSalesStageAudit = (filters: SalesStageFilters) => {
  const apiClient = useApiClient();

  const searchRequest: SearchRequest = useMemo(() => ({
    pagination: { limit: DEFAULT_LIMIT, page: 1 },
    filterBy: buildFilterBy(filters),
    sortBy: [{ field: 'actionTakenAt', order: 'desc' }],
  }), [filters]);

  const {
    data,
    error,
    hasMore,
    isLoading,
    isValidating,
    loadMore,
  } = useFetchInfiniteES<SalesStageAudit>(
    apiClient.trackingClient.postSalesStageAuditSearches,
    searchRequest,
    { revalidateOnFocus: false },
  );

  const audits = useMemo(
    () => data?.flatMap((sr: SearchResult<SalesStageAudit>) => sr.hits) ?? [],
    [data],
  );

  return {
    audits,
    error,
    hasMore,
    isLoading: isLoading || isValidating,
    loadMore,
    totalHits: data?.[0]?.totalHits ?? 0,
  };
};

export default useSalesStageAudit;
